import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Button } from './ui/button';
import { Menu, X } from 'lucide-react';

export const Header = ({ onConsultationClick }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId) => {
    setIsMobileMenuOpen(false);
    if (location.pathname !== '/') {
      // Navigate home and then scroll
      navigate('/');
      setTimeout(() => {
        const element = document.getElementById(sectionId);
        if (element) element.scrollIntoView({ behavior: 'smooth' });
      }, 200);
      return;
    }
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <header 
      className="sticky top-12 left-0 right-0 z-[9998] bg-white shadow-md border-b border-slate-200 transition-all duration-300"
    >
      <div className="container mx-auto px-6 lg:px-12">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <div className="flex items-center">
            <Link to="/">
              <img 
                src="https://customer-assets.emergentagent.com/job_videocraft-learn/artifacts/ry8c08iu_PRS%20Banner%20%282%29.png" 
                alt="MatchFrame Logo" 
                className="h-24 md:h-32 w-auto transition-transform duration-300 hover:scale-105"
                style={{ maxWidth: '500px' }}
                loading="eager"
              />
            </Link>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-8">
            <button 
              onClick={() => scrollToSection('courses')}
              className="font-semibold transition-colors duration-300 text-slate-900 hover:text-purple-600"
            >
              Courses
            </button>
            <Link 
              to="/hiring-partners"
              className="font-semibold transition-colors duration-300 text-slate-900 hover:text-purple-600"
              data-testid="header-hire-editors-link"
            >
              Hire Editors
            </Link>
            <button 
              onClick={() => scrollToSection('testimonials')}
              className="font-semibold transition-colors duration-300 text-slate-900 hover:text-purple-600"
            >
              Success Stories
            </button>
            <button 
              onClick={() => scrollToSection('faq')}
              className="font-semibold transition-colors duration-300 text-slate-900 hover:text-purple-600"
            >
              FAQ
            </button>
          </nav>

          {/* CTA Button */}
          <div className="hidden md:block">
            <Button 
              onClick={() => window.location.href = 'tel:+919632406669'}
              className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white px-6 py-2.5 rounded-full font-semibold transform hover:scale-105 transition-all duration-300 border-0"
            >
              Call Us
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button 
            className="md:hidden p-2"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            {isMobileMenuOpen ? (
              <X className="h-6 w-6 text-slate-900" />
            ) : (
              <Menu className="h-6 w-6 text-slate-900" />
            )}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="md:hidden py-6 space-y-4 bg-white rounded-2xl mt-4 px-6 shadow-xl border border-slate-200">
            <button 
              onClick={() => scrollToSection('courses')}
              className="block w-full text-left font-semibold text-slate-900 hover:text-purple-600 py-2"
            >
              Courses
            </button>
            <Link
              to="/hiring-partners"
              onClick={() => setIsMobileMenuOpen(false)}
              className="block w-full text-left font-semibold text-slate-900 hover:text-purple-600 py-2"
            >
              Hire Editors
            </Link>
            <button 
              onClick={() => scrollToSection('testimonials')}
              className="block w-full text-left font-semibold text-slate-900 hover:text-purple-600 py-2"
            >
              Success Stories
            </button>
            <button 
              onClick={() => scrollToSection('faq')}
              className="block w-full text-left font-semibold text-slate-900 hover:text-purple-600 py-2"
            >
              FAQ
            </button>
            <button 
              onClick={() => scrollToSection('contact')}
              className="block w-full text-left font-semibold text-slate-900 hover:text-purple-600 py-2"
            >
              Contact
            </button>
            <Button 
              onClick={() => window.location.href = 'tel:+919632406669'}
              className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white py-3 rounded-full font-semibold border-0"
            >
              Call Us
            </Button>
          </div>
        )}
      </div>
    </header>
  );
};